import { useState, useEffect, useMemo, useCallback } from 'react'
import { DashboardLayout } from '../components/DashboardLayout'
import { FilterTabs } from '../components/FilterTabs'
import { PageTabs } from '../components/PageTabs'
import { api, type AccessRequest, type Booking, type Room, type RoomSchedule } from '../lib/api'
import { formatSlotRange } from '../lib/utils'
import { useAuthStore } from '../lib/auth'
import { subscribeToSSE } from '../components/NotificationBell'
import { 
  UserPlus, 
  Mail, 
  Phone, 
  Calendar, 
  MessageSquare, 
  X, 
  Loader2,
  Building2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Pencil,
  Check,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'

type StatusFilter = 'all' | 'pending' | 'approved' | 'denied'

export function RequestsPage() {
  const { currentOrg } = useAuthStore()

  // Admin-only page (the Requests tab of Bookings)
  useEffect(() => {
    if (currentOrg && currentOrg.role !== 'admin') {
      window.location.replace('/dashboard')
    }
  }, [currentOrg])

  const [requests, setRequests] = useState<AccessRequest[]>([])
  const [rooms, setRooms] = useState<Room[]>([])
  const [roomSchedules, setRoomSchedules] = useState<Record<string, RoomSchedule[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending')
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const loadRequests = useCallback(async () => {
    if (!currentOrg) return
    
    setLoading(true)
    try {
      const { requests: data } = await api.bookings.listAccessRequests()
      setRequests(data)
    } catch (err) {
      setError('Failed to load access requests')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [currentOrg])

  useEffect(() => {
    loadRequests()
    loadRooms()
  }, [currentOrg?.orgId, loadRequests])

  // Subscribe to SSE for real-time updates when new access requests come in
  useEffect(() => {
    const unsubscribe = subscribeToSSE('notification', (data: { notificationType?: string }) => {
      // Reload requests when we get an access_request notification
      if (data.notificationType === 'access_request' || data.notificationType === 'booking_request') {
        loadRequests()
      }
    })
    
    return () => unsubscribe()
  }, [loadRequests])

  async function loadRooms() {
    if (!currentOrg) return
    try {
      const { rooms: data } = await api.rooms.list()
      setRooms(data)
      // Load schedules for all rooms
      const scheduleMap: Record<string, RoomSchedule[]> = {}
      await Promise.all(
        data.map(async (room) => {
          try {
            const { schedules } = await api.rooms.getSchedule(room.id)
            scheduleMap[room.id] = schedules
          } catch {
            // ignore - room might not have schedules
          }
        })
      )
      setRoomSchedules(scheduleMap)
    } catch (err) {
      console.error('Failed to load rooms:', err)
    }
  }

  async function handleApprove(request: AccessRequest) {
    setActionLoading(request.id)
    try {
      await api.bookings.updateAccessRequest(request.id, { status: 'approved' })
      // Mark any related notifications as read
      await api.notifications.markByReference({ requestId: request.id })
      setRequests(prev => prev.map(r => 
        r.id === request.id 
          ? { ...r, status: 'approved' as const, handledAt: new Date().toISOString() }
          : r
      ))
    } catch (err) {
      console.error('Failed to approve request:', err)
    } finally {
      setActionLoading(null)
    }
  }

  // Re-issue an invite for an approved-but-stranded request (re-approving creates a fresh
  // invite and emails the guest). Reload afterwards so the booking state badge updates.
  async function handleReinvite(request: AccessRequest) {
    setActionLoading(request.id)
    try {
      await api.bookings.updateAccessRequest(request.id, { status: 'approved' })
      await loadRequests()
    } catch (err) {
      console.error('Failed to re-send invite:', err)
    } finally {
      setActionLoading(null)
    }
  }

  async function handleDeny(request: AccessRequest) {
    setActionLoading(request.id)
    try {
      await api.bookings.updateAccessRequest(request.id, { status: 'denied' })
      // Mark any related notifications as read
      await api.notifications.markByReference({ requestId: request.id })
      setRequests(prev => prev.map(r => 
        r.id === request.id 
          ? { ...r, status: 'denied' as const, handledAt: new Date().toISOString() }
          : r
      ))
    } catch (err) {
      console.error('Failed to deny request:', err)
    } finally {
      setActionLoading(null)
    }
  }

  async function handleUpdateRequest(requestId: string, updates: {
    roomId?: string
    preferredDate?: string
    preferredTime?: string
  }) {
    setActionLoading(requestId)
    try {
      const result = await api.bookings.updateAccessRequest(requestId, updates)
      if (result.request) {
        setRequests(prev => prev.map(r => 
          r.id === requestId ? { ...r, ...result.request } : r
        ))
      } else {
        // Reload to get updated data
        await loadRequests()
      }
    } catch (err) {
      console.error('Failed to update request:', err)
    } finally {
      setActionLoading(null)
    }
  }

  const filteredRequests = requests.filter(r => {
    if (statusFilter === 'all') return true
    return r.status === statusFilter
  })

  const pendingCount = requests.filter(r => r.status === 'pending').length

  if (loading) {
    return (
      <DashboardLayout title="Access Requests">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      </DashboardLayout>
    )
  }

  if (error) {
    return (
      <DashboardLayout title="Access Requests">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-red-600">{error}</p>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout
      title="Bookings"
      subtitle={`${pendingCount} pending request${pendingCount !== 1 ? 's' : ''}`}
    >
      <PageTabs
        tabs={[
          { label: 'Bookings', to: '/dashboard/bookings' },
          { label: 'Requests', to: '/dashboard/requests' },
        ]}
        active="/dashboard/requests"
      />
      <FilterTabs
        options={[
          { value: 'pending', label: 'Pending', count: pendingCount, countColor: 'amber' },
          { value: 'approved', label: 'Approved' },
          { value: 'denied', label: 'Denied' },
          { value: 'all', label: 'All' },
        ]}
        value={statusFilter}
        onChange={(value) => setStatusFilter(value as StatusFilter)}
      />

      {filteredRequests.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 sm:p-12 text-center">
          <UserPlus className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-3 sm:mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
            {statusFilter === 'pending' ? 'No pending requests' : 'No requests found'}
          </h3>
          <p className="text-sm sm:text-base text-gray-500">
            {statusFilter === 'pending' 
              ? 'When someone requests access to book your rooms, they will appear here.'
              : `No ${statusFilter} requests to show.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRequests.map((request) => (
            <RequestCard
              key={request.id}
              request={request}
              rooms={rooms}
              roomSchedules={roomSchedules}
              isExpanded={expandedId === request.id}
              onToggle={() => setExpandedId(expandedId === request.id ? null : request.id)}
              onApprove={() => handleApprove(request)}
              onDeny={() => handleDeny(request)}
              onReinvite={() => handleReinvite(request)}
              onUpdate={(updates) => handleUpdateRequest(request.id, updates)}
              isLoading={actionLoading === request.id}
            />
          ))}
        </div>
      )}
    </DashboardLayout>
  )
}

function RequestCard({
  request,
  rooms,
  roomSchedules,
  isExpanded,
  onToggle,
  onApprove,
  onDeny,
  onReinvite,
  onUpdate,
  isLoading,
}: {
  request: AccessRequest
  rooms: Room[]
  roomSchedules: Record<string, RoomSchedule[]>
  isExpanded: boolean
  onToggle: () => void
  onApprove: () => void
  onDeny: () => void
  onReinvite: () => void
  onUpdate: (updates: { roomId?: string; preferredDate?: string; preferredTime?: string }) => void
  isLoading: boolean
}) {
  const isPending = request.status === 'pending'
  const [isEditing, setIsEditing] = useState(false)
  const [editRoomId, setEditRoomId] = useState(request.roomId || '')
  const [editDate, setEditDate] = useState(request.preferredDate || '')
  const [editCalendarMonth, setEditCalendarMonth] = useState(() => {
    if (request.preferredDate) {
      const d = new Date(request.preferredDate + 'T12:00:00')
      return new Date(d.getFullYear(), d.getMonth(), 1)
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  })
  const [editSelectedSlots, setEditSelectedSlots] = useState<Set<string>>(new Set())
  
  // Sync edit state when request changes
  useEffect(() => {
    setEditRoomId(request.roomId || '')
    setEditDate(request.preferredDate || '')
  }, [request.roomId, request.preferredDate])
  
  const requestedSlots = useMemo(() => {
    if (!request.preferredTime) return []
    // preferredTime may contain raw slots after pipe: "2:00 PM, 3:00 PM|14:00,15:00"
    // Extract only the formatted display part (before the pipe)
    const displayTime = request.preferredTime.includes('|') 
      ? request.preferredTime.split('|')[0] 
      : request.preferredTime
    return displayTime ? displayTime.split(', ').map(s => s.trim()) : []
  }, [request.preferredTime])

  // Check if room is closed on the requested day
  const isClosedOnRequestedDay = useMemo(() => {
    if (!request.preferredDate || !request.roomId) return false
    const schedules = roomSchedules[request.roomId]
    if (!schedules || schedules.length === 0) return false
    const dayOfWeek = new Date(request.preferredDate + 'T12:00:00').getDay()
    return !schedules.find(s => s.dayOfWeek === dayOfWeek && s.isActive)
  }, [request.preferredDate, request.roomId, roomSchedules])
  
  return (
    <div className={`bg-white rounded-xl border ${
      isPending ? 'border-amber-200' : 'border-gray-200'
    } overflow-hidden`}>
      <button
        onClick={onToggle}
        className="w-full p-3 sm:p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            isPending ? 'bg-amber-100' : 
            request.status === 'approved' ? 'bg-green-100' : 'bg-red-100'
          }`}>
            <UserPlus className={`w-4 h-4 sm:w-5 sm:h-5 ${
              isPending ? 'text-amber-600' : 
              request.status === 'approved' ? 'text-green-600' : 'text-red-600'
            }`} />
          </div>
          <div className="text-left min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{request.name}</h3>
            <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500">
              {request.roomName && (
                <span className="text-primary-600 font-medium truncate max-w-[100px] sm:max-w-none">{request.roomName}</span>
              )}
              {request.roomName && request.preferredDate && <span className="hidden sm:inline">·</span>}
              {request.preferredDate && (
                <span className="hidden sm:inline">
                  {new Date(request.preferredDate).toLocaleDateString('en-GB', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short'
                  })}
                </span>
              )}
              {requestedSlots.length > 0 && (
                <>
                  <span className="hidden sm:inline">·</span>
                  <span className="text-amber-600 font-medium">{requestedSlots.length} slots</span>
                </>
              )}
              {isClosedOnRequestedDay && (
                <>
                  <span className="hidden sm:inline">·</span>
                  <span className="text-red-600 font-medium">Closed</span>
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <span className="hidden sm:inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
            Guest
          </span>
          <span className={`px-1.5 sm:px-2.5 py-0.5 sm:py-1 text-xs font-medium rounded-full ${
            isPending ? 'bg-amber-100 text-amber-700' :
            request.status === 'approved' ? 'bg-green-100 text-green-700' :
            'bg-red-100 text-red-700'
          }`}>
            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
          </span>
          {request.status === 'approved' && request.bookingState && (
            <span className={`px-1.5 sm:px-2.5 py-0.5 sm:py-1 text-xs font-medium rounded-full ${
              request.bookingState === 'booked' ? 'bg-green-100 text-green-700' :
              request.bookingState === 'awaiting' ? 'bg-blue-100 text-blue-700' :
              'bg-red-100 text-red-700'
            }`}>
              {request.bookingState === 'booked' ? 'Booked' :
                request.bookingState === 'awaiting' ? 'Awaiting booking' :
                'Not booked'}
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-100 p-3 sm:p-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                  <a href={`mailto:${request.email}`} className="text-primary-600 hover:underline truncate">
                    {request.email}
                  </a>
                </div>
                {request.phone && (
                  <div className="flex items-center gap-2 text-xs sm:text-sm">
                    <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                    <a href={`tel:${request.phone}`} className="text-gray-700 hover:underline">
                      {request.phone}
                    </a>
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                  <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                  <span>Requested {new Date(request.createdAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                </div>
              </div>

              {request.message && (
                <div className="bg-gray-50 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
                  <div className="flex items-start gap-2">
                    <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 mt-0.5 shrink-0" />
                    <p className="text-xs sm:text-sm text-gray-600 italic line-clamp-3">"{request.message}"</p>
                  </div>
                </div>
              )}

              {request.handledAt && (
                <div className="text-xs text-gray-500 mb-3 sm:mb-4">
                  {request.status === 'approved' ? 'Approved' : 'Denied'} 
                  {request.handledByName && ` by ${request.handledByName}`} on{' '}
                  {new Date(request.handledAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </div>
              )}

              {request.bookingState === 'not_booked' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
                  <p className="text-xs sm:text-sm text-red-800 flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5" />
                    <span>
                      This was approved but never became a booking, and the guest has no
                      outstanding invite. Re-send the invite so they can complete it, or add the
                      booking for them directly from the dashboard.
                    </span>
                  </p>
                </div>
              )}

              {!isPending && !isEditing && (
                <div className="pt-2 sm:pt-3 border-t border-gray-100">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setIsEditing(true)
                        if (request.preferredTime) {
                          const raw = request.preferredTime.includes('|')
                            ? request.preferredTime.split('|')[1]
                            : null
                          if (raw) {
                            setEditSelectedSlots(new Set(raw.split(',').map(s => s.trim())))
                          } else {
                            setEditSelectedSlots(new Set())
                          }
                        }
                      }}
                      className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 text-gray-700 text-xs sm:text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Edit Request
                    </button>
                    {request.status === 'approved' && request.bookingState === 'not_booked' && (
                      <button
                        onClick={onReinvite}
                        disabled={isLoading}
                        className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 bg-primary-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                      >
                        {isLoading ? (
                          <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                        ) : (
                          <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        )}
                        Re-send invite
                      </button>
                    )}
                  </div>
                </div>
              )}

              {isPending && (
                <div className="pt-2 sm:pt-3 border-t border-gray-100">
                  {isClosedOnRequestedDay && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">
                      <p className="text-xs text-amber-800 flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                        This booking is outside normal hours. Approving will grant the guest permission to book outside normal hours.
                      </p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                  <a
                    href={`mailto:${request.email}?subject=Your Access Request to ${request.roomName || 'our space'}`}
                    className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 text-gray-700 text-xs sm:text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Contact</span>
                  </a>
                  <button
                    onClick={() => {
                      setIsEditing(true)
                      // Initialize edit slots from current request
                      if (request.preferredTime) {
                        const raw = request.preferredTime.includes('|') 
                          ? request.preferredTime.split('|')[1] 
                          : null
                        if (raw) {
                          setEditSelectedSlots(new Set(raw.split(',').map(s => s.trim())))
                        } else {
                          setEditSelectedSlots(new Set())
                        }
                      }
                    }}
                    className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 text-gray-700 text-xs sm:text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Edit Request</span>
                    <span className="sm:hidden">Edit</span>
                  </button>
                  <button
                    onClick={onApprove}
                    disabled={isLoading}
                    className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 bg-primary-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {isLoading ? <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" /> : <UserPlus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                    <span className="hidden sm:inline">Invite to Book</span>
                    <span className="sm:hidden">Invite</span>
                  </button>
                  <button
                    onClick={onDeny}
                    disabled={isLoading}
                    className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 text-gray-700 text-xs sm:text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    Decline
                  </button>
                  </div>
                </div>
              )}
            </div>

            {isEditing ? (
              <EditRequestPanel
                rooms={rooms}
                roomSchedules={roomSchedules}
                editRoomId={editRoomId}
                setEditRoomId={setEditRoomId}
                editDate={editDate}
                setEditDate={setEditDate}
                editCalendarMonth={editCalendarMonth}
                setEditCalendarMonth={setEditCalendarMonth}
                editSelectedSlots={editSelectedSlots}
                setEditSelectedSlots={setEditSelectedSlots}
                isLoading={isLoading}
                onSave={() => {
                  // Build 12h display slots and 24h raw slots
                  const sortedSlots = [...editSelectedSlots].sort()
                  const display = sortedSlots.map(t => {
                    const [h, m] = t.split(':')
                    const hour = parseInt(h)
                    const ampm = hour >= 12 ? 'PM' : 'AM'
                    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
                    return `${h12}:${m} ${ampm}`
                  }).join(', ')
                  const raw = sortedSlots.join(',')
                  const preferredTime = sortedSlots.length > 0 ? `${display}|${raw}` : ''
                  
                  onUpdate({
                    roomId: editRoomId || undefined,
                    preferredDate: editDate || undefined,
                    preferredTime,
                  })
                  setIsEditing(false)
                }}
                onCancel={() => {
                  setIsEditing(false)
                  setEditRoomId(request.roomId || '')
                  setEditDate(request.preferredDate || '')
                }}
              />
            ) : request.preferredDate ? (
              <div className="hidden lg:block">
                <DayCalendarView 
                  date={request.preferredDate}
                  rooms={rooms}
                  requestedRoomId={request.roomId || null}
                  requestedSlots={requestedSlots}
                  schedules={request.roomId ? roomSchedules[request.roomId] || [] : []}
                />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}

function EditRequestPanel({
  rooms,
  roomSchedules,
  editRoomId,
  setEditRoomId,
  editDate,
  setEditDate,
  editCalendarMonth,
  setEditCalendarMonth,
  editSelectedSlots,
  setEditSelectedSlots,
  isLoading,
  onSave,
  onCancel,
}: {
  rooms: Room[]
  roomSchedules: Record<string, RoomSchedule[]>
  editRoomId: string
  setEditRoomId: (v: string) => void
  editDate: string
  setEditDate: (v: string) => void
  editCalendarMonth: Date
  setEditCalendarMonth: (v: Date) => void
  editSelectedSlots: Set<string>
  setEditSelectedSlots: (v: Set<string>) => void
  isLoading: boolean
  onSave: () => void
  onCancel: () => void
}) {
  const schedules = editRoomId ? roomSchedules[editRoomId] || [] : []

  // Admins can reveal slots outside the room's available hours
  const [showAllHours, setShowAllHours] = useState(false)

  // Check if selected date's day is open
  const editDayOfWeek = editDate ? new Date(editDate + 'T12:00:00').getDay() : -1
  const editDaySchedule = schedules.find(s => s.dayOfWeek === editDayOfWeek && s.isActive)
  const isEditDayClosed = editDate && schedules.length > 0 && !editDaySchedule

  // Generate time slots based on schedule
  const availableTimeSlots = useMemo(() => {
    if (!editDaySchedule) return []
    const slots: string[] = []
    const [openH, openM] = editDaySchedule.openTime.split(':').map(Number)
    const [closeH, closeM] = editDaySchedule.closeTime.split(':').map(Number)
    let h = openH, m = openM
    while (h < closeH || (h === closeH && m < closeM)) {
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
      m += 30
      if (m >= 60) { m = 0; h++ }
    }
    return slots
  }, [editDaySchedule])

  // Full 24h grid (every 30 mins) used when revealing slots outside available hours
  const allDaySlots = useMemo(
    () =>
      Array.from({ length: 24 }, (_, h) => [
        `${h.toString().padStart(2, '0')}:00`,
        `${h.toString().padStart(2, '0')}:30`,
      ]).flat(),
    []
  )

  // Whether a slot falls within the selected day's normal opening hours
  const isSlotWithinSchedule = (slot: string): boolean => {
    if (!editDaySchedule) return false
    const toMin = (t: string) => {
      const [h, m] = t.split(':')
      return parseInt(h || '0') * 60 + parseInt(m || '0')
    }
    const slotMin = toMin(slot)
    return slotMin >= toMin(editDaySchedule.openTime) && slotMin < toMin(editDaySchedule.closeTime)
  }

  // Calendar
  const calendarDays = useMemo(() => {
    const year = editCalendarMonth.getFullYear()
    const month = editCalendarMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const days: (Date | null)[] = []
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null)
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d))
    return days
  }, [editCalendarMonth])

  const isPastDate = (date: Date) => {
    return date < new Date(new Date().setHours(0, 0, 0, 0))
  }

  const hasAvailability = (date: Date) => {
    if (schedules.length === 0) return true
    const dow = date.getDay()
    return !!schedules.find(s => s.dayOfWeek === dow && s.isActive)
  }

  const formatDateStr = (d: Date) => {
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`
  }

  const toggleSlot = (slot: string) => {
    const next = new Set(editSelectedSlots)
    if (next.has(slot)) next.delete(slot)
    else next.add(slot)
    setEditSelectedSlots(next)
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-blue-900">Edit Request</h4>
      </div>

      {/* Room selector */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-700 mb-1">Room</label>
        <select
          value={editRoomId}
          onChange={(e) => {
            setEditRoomId(e.target.value)
            setEditSelectedSlots(new Set())
          }}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">Select a room</option>
          {rooms.filter(r => r.isPublic).map(room => (
            <option key={room.id} value={room.id}>{room.name}</option>
          ))}
        </select>
      </div>

      {/* Mini calendar */}
      {editRoomId && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
          <div className="bg-white rounded-lg border border-gray-200 p-2">
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={() => setEditCalendarMonth(new Date(editCalendarMonth.getFullYear(), editCalendarMonth.getMonth() - 1, 1))}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronLeft className="w-4 h-4 text-gray-500" />
              </button>
              <span className="text-xs font-medium text-gray-700">
                {editCalendarMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
              </span>
              <button
                onClick={() => setEditCalendarMonth(new Date(editCalendarMonth.getFullYear(), editCalendarMonth.getMonth() + 1, 1))}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-0.5 text-center">
              {['S','M','T','W','T','F','S'].map((d, i) => (
                <div key={i} className="text-[10px] font-medium text-gray-400 py-0.5">{d}</div>
              ))}
              {calendarDays.map((day, i) => {
                if (!day) return <div key={`empty-${i}`} />
                const dateStr = formatDateStr(day)
                const past = isPastDate(day)
                const available = hasAvailability(day)
                const isSelected = dateStr === editDate
                const isToday = formatDateStr(new Date()) === dateStr
                return (
                  <button
                    key={dateStr}
                    onClick={() => {
                      if (!past) {
                        setEditDate(dateStr)
                        setEditSelectedSlots(new Set())
                      }
                    }}
                    disabled={past}
                    className={`text-xs py-1 rounded transition-colors ${
                      isSelected
                        ? available
                          ? 'bg-primary-600 text-white font-bold'
                          : 'bg-amber-500 text-white font-bold'
                        : past
                          ? 'text-gray-300 cursor-not-allowed'
                          : available
                            ? isToday
                              ? 'bg-primary-50 text-primary-700 font-medium hover:bg-primary-100'
                              : 'text-gray-700 hover:bg-gray-100'
                            : isToday
                              ? 'bg-amber-50 text-amber-600 font-medium hover:bg-amber-100'
                              : 'text-amber-400 hover:bg-amber-50'
                    }`}
                  >
                    {day.getDate()}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Schedule warning */}
      {isEditDayClosed && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
          <p className="text-xs text-amber-800 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            This day is outside normal hours. The guest will be given permission to book outside normal hours.
          </p>
        </div>
      )}

      {/* Time slots for closed days - full day available */}
      {editDate && isEditDayClosed && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Time Slots (outside normal hours)
          </label>
          <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
            {Array.from({ length: 24 }, (_, h) => [
              `${h.toString().padStart(2, '0')}:00`,
              `${h.toString().padStart(2, '0')}:30`,
            ]).flat().map(slot => {
              const selected = editSelectedSlots.has(slot)
              return (
                <button
                  key={slot}
                  onClick={() => toggleSlot(slot)}
                  className={`px-2 py-1.5 text-xs rounded border transition-colors whitespace-nowrap ${
                    selected
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                  }`}
                >
                  {formatSlotRange(slot)}
                </button>
              )
            })}
          </div>
          {editSelectedSlots.size > 0 && (
            <p className="text-xs text-amber-600 mt-1">{editSelectedSlots.size} slot{editSelectedSlots.size !== 1 ? 's' : ''} selected (outside normal hours)</p>
          )}
        </div>
      )}

      {/* Time slots for open days */}
      {editDate && editDaySchedule && !isEditDayClosed && (
        <div className="mb-3">
          {/* Admin toggle: reveal slots outside the room's available hours */}
          <label className="flex items-center justify-between gap-3 mb-2 p-2 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
            <span className="text-xs">
              <span className="font-medium text-amber-900">Show unavailable slots</span>
              <span className="block text-amber-700">Set a time outside the room's available hours</span>
            </span>
            <input
              type="checkbox"
              checked={showAllHours}
              onChange={(e) => {
                setShowAllHours(e.target.checked)
                // Hiding again drops any out-of-hours selections that would no longer be visible
                if (!e.target.checked) {
                  setEditSelectedSlots(new Set([...editSelectedSlots].filter(isSlotWithinSchedule)))
                }
              }}
              className="h-4 w-4 rounded border-amber-300 text-primary-600 focus:ring-primary-500"
            />
          </label>

          <label className="block text-xs font-medium text-gray-700 mb-1">
            Time Slots{' '}
            {showAllHours ? '(all hours)' : `(${editDaySchedule.openTime} - ${editDaySchedule.closeTime})`}
          </label>
          <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
            {(showAllHours ? allDaySlots : availableTimeSlots).map(slot => {
              const selected = editSelectedSlots.has(slot)
              const outOfHours = showAllHours && !isSlotWithinSchedule(slot)
              return (
                <button
                  key={slot}
                  onClick={() => toggleSlot(slot)}
                  title={outOfHours ? 'Outside available hours' : undefined}
                  className={`px-2 py-1.5 text-xs rounded border transition-colors whitespace-nowrap ${
                    selected
                      ? outOfHours
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-primary-600 text-white border-primary-600'
                      : outOfHours
                        ? 'bg-white text-amber-700 border-dashed border-amber-300 hover:bg-amber-50'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                  }`}
                >
                  {formatSlotRange(slot)}
                </button>
              )
            })}
          </div>
          {editSelectedSlots.size > 0 && (
            <p className="text-xs text-gray-500 mt-1">{editSelectedSlots.size} slot{editSelectedSlots.size !== 1 ? 's' : ''} selected</p>
          )}
        </div>
      )}

      {/* Outside hours notice */}
      {isEditDayClosed && editSelectedSlots.size > 0 && (
        <div className="bg-blue-50 border border-blue-300 rounded-lg p-2 mb-3">
          <p className="text-xs text-blue-800">
            When approved, this guest will be granted permission to book outside normal hours.
          </p>
        </div>
      )}

      {/* Save / Cancel */}
      <div className="flex gap-2 pt-2 border-t border-blue-200">
        <button
          onClick={onSave}
          disabled={isLoading || !editRoomId || !editDate}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Save Changes
        </button>
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function DayCalendarView({
  date,
  rooms,
  requestedRoomId,
  requestedSlots,
  schedules,
}: {
  date: string
  rooms: Room[]
  requestedRoomId: string | null
  requestedSlots: string[]
  schedules: RoomSchedule[]
}) {
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDayBookings()
  }, [date])

  async function loadDayBookings() {
    setLoading(true)
    try {
      const startDate = new Date(date)
      startDate.setHours(0, 0, 0, 0)
      const endDate = new Date(date)
      endDate.setHours(23, 59, 59, 999)
      
      const { bookings: data } = await api.bookings.list({
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      })
      setBookings(data)
    } catch (err) {
      console.error('Failed to load bookings:', err)
    } finally {
      setLoading(false)
    }
  }

  // Check if the room is open on the requested day
  const requestDate = new Date(date + 'T12:00:00')
  const dayOfWeek = requestDate.getDay()
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const daySchedule = schedules.find(s => s.dayOfWeek === dayOfWeek && s.isActive)
  const isRoomClosed = schedules.length > 0 && !daySchedule

  // Build list of open days for display
  const openDays = schedules
    .filter(s => s.isActive)
    .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
    .map(s => ({
      day: dayNames[s.dayOfWeek],
      shortDay: dayNames[s.dayOfWeek].slice(0, 3),
      open: s.openTime,
      close: s.closeTime,
    }))

  const timeSlots = useMemo(() => {
    const slots: string[] = []
    for (let h = 7; h <= 21; h++) {
      slots.push(`${h.toString().padStart(2, '0')}:00`)
      slots.push(`${h.toString().padStart(2, '0')}:30`)
    }
    return slots
  }, [])

  const formatTo12h = (time24: string) => {
    const [h, m] = time24.split(':')
    const hour = parseInt(h || '0')
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${hour12}:${m} ${ampm}`
  }

  // Convert 12h time to 24h for comparison
  const parseTo24h = (time12: string) => {
    const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
    if (!match) return '99:99'
    let hour = parseInt(match[1])
    const min = match[2]
    const ampm = match[3].toUpperCase()
    if (ampm === 'PM' && hour !== 12) hour += 12
    if (ampm === 'AM' && hour === 12) hour = 0
    return `${hour.toString().padStart(2, '0')}:${min}`
  }

  const isRequestedSlot = (time24: string) => {
    const time12 = formatTo12h(time24)
    return requestedSlots.includes(time12)
  }

  // Check if a time slot falls within opening hours
  const isWithinOpeningHours = (time24: string) => {
    if (!daySchedule) return false
    return time24 >= daySchedule.openTime && time24 < daySchedule.closeTime
  }

  // Find the first requested slot to focus the view
  const firstRequestedTime = useMemo(() => {
    if (requestedSlots.length === 0) return null
    const sorted = [...requestedSlots].sort((a, b) => parseTo24h(a).localeCompare(parseTo24h(b)))
    return parseTo24h(sorted[0])
  }, [requestedSlots])

  // Filter time slots to start from one hour before the first requested slot
  const focusedTimeSlots = useMemo(() => {
    if (!firstRequestedTime) return timeSlots
    
    // Find the index of the first requested slot
    const firstIndex = timeSlots.findIndex(t => t >= firstRequestedTime)
    if (firstIndex <= 0) return timeSlots
    
    // Start 2 slots (1 hour) before the first requested slot, minimum index 0
    const startIndex = Math.max(0, firstIndex - 2)
    return timeSlots.slice(startIndex)
  }, [timeSlots, firstRequestedTime])

  const getBookingAt = (roomId: string, time: string) => {
    return bookings.find(b => {
      if (b.roomId !== roomId) return false
      const bookingStart = b.startTime.slice(11, 16)
      const bookingEnd = b.endTime.slice(11, 16)
      return time >= bookingStart && time < bookingEnd
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  // Get the requested room info
  const requestedRoom = rooms.find(r => r.id === requestedRoomId)

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-4 h-4 text-gray-500" />
        <h4 className="text-sm font-semibold text-gray-700">
          {new Date(date + 'T12:00:00').toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
          })}
        </h4>
      </div>

      {requestedRoom && (
        <p className="text-xs text-gray-500 mb-3">{requestedRoom.name}</p>
      )}

      {/* Closed warning */}
      {isRoomClosed && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">
                Closed on {dayNames[dayOfWeek]}s
              </p>
              <p className="text-xs text-red-600 mt-1">
                This room is not open on the requested day.
                {openDays.length > 0 && (
                  <> Open: {openDays.map(d => `${d.shortDay} ${d.open}-${d.close}`).join(', ')}</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Open hours info when room IS open */}
      {daySchedule && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-3">
          <p className="text-xs text-green-700 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            Open {formatTo12h(daySchedule.openTime)} - {formatTo12h(daySchedule.closeTime)}
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-3 text-xs">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-amber-400 border border-amber-500"></span>
          Requested
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-blue-400"></span>
          Booked
        </span>
      </div>

      {/* Vertical time slots */}
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {focusedTimeSlots.map((time) => {
          const isRequested = isRequestedSlot(time)
          const booking = requestedRoomId ? getBookingAt(requestedRoomId, time) : null
          const withinHours = isWithinOpeningHours(time)
          
          // Determine styling
          let bgColor = 'bg-white'
          let textColor = 'text-gray-400'
          let label = ''
          
          if (isRequested && isRoomClosed) {
            bgColor = 'bg-red-50 border-red-200'
            textColor = 'text-red-600'
            label = 'Closed'
          } else if (isRequested && !withinHours && schedules.length > 0) {
            bgColor = 'bg-red-50 border-red-200'
            textColor = 'text-red-600'
            label = 'Outside hours'
          } else if (isRequested) {
            bgColor = 'bg-amber-100 border-amber-300'
            textColor = 'text-amber-700'
            label = 'Requested'
          } else if (booking) {
            bgColor = 'bg-blue-100 border-blue-300'
            textColor = 'text-blue-700'
            label = booking.guestName || 'Booked'
          } else if (schedules.length > 0 && !withinHours) {
            bgColor = 'bg-gray-50 border-gray-200'
            textColor = 'text-gray-300'
          }
          
          return (
            <div
              key={time}
              className={`flex items-center justify-between px-3 py-1.5 rounded border ${bgColor}`}
            >
              <span className={`text-sm font-medium ${textColor}`}>
                {formatTo12h(time)}
              </span>
              {label && (
                <span className={`text-xs ${textColor}`}>
                  {label}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
